import { useState } from "react";
import { Check, Handshake } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { Field, inputClasses } from "./GetFeatured";
import { api } from "../../lib/api";

const OFFERINGS = [
  "SPONSORSHIPS",
  "ADVERTISING",
  "BRAND PARTNERSHIPS",
  "PRODUCT FEATURES",
  "SPONSORED CONTENT",
  "CULTURE CAMPAIGNS",
  "MUSIC / CONTENT PREMIERES",
  "CUSTOM COLLABORATIONS",
];

const PIPELINE = ["INQUIRY", "AGREEMENT", "INVOICE", "PAYMENT", "CONFIRMATION"];

export default function Partner() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", website: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Name required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Valid email required.";
    if (form.message.trim().length < 5) errs.message = "Tell us what you have in mind.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("loading");
    try {
      await api.sendInquiry({ type: "partner", ...form });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="partner" className="relative py-24 md:py-36 bg-obsidian" data-testid="partner-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="border border-tan/25 bg-wood/60 p-7 md:p-14" data-testid="partner-panel">
          <div className="grid lg:grid-cols-2 gap-14">
            <Reveal>
              <Eyebrow index="12" label="FOR BRANDS" />
              <h2 className="font-display text-cream text-5xl md:text-7xl tracking-tight leading-[0.88]" data-testid="partner-heading">
                PARTNER WITH <span className="text-gold-bright">COBAIN.</span>
              </h2>
              <p className="mt-6 text-sand font-light leading-relaxed max-w-md">
                The Cobain Report has the audience, the platform and the culture's attention. There are a few ways for brands to participate — all of them premium, all of them intentional.
              </p>
              <div className="mt-8 flex flex-wrap gap-2" data-testid="partner-offerings">
                {OFFERINGS.map((o) => (
                  <span key={o} className="font-mono text-[10px] tracking-[0.18em] border border-tan/30 text-tan px-4 py-2 rounded-full">
                    {o}
                  </span>
                ))}
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-2 font-mono text-[10px] tracking-[0.2em]" data-testid="partner-pipeline">
                {PIPELINE.map((p, i) => (
                  <span key={p} className="flex items-center gap-2">
                    <span className={i === 3 ? "text-gold-bright" : "text-clay"}>{p}</span>
                    {i < PIPELINE.length - 1 && <span className="text-burnt">→</span>}
                  </span>
                ))}
              </div>
              <p className="mt-3 font-mono text-[9px] tracking-[0.15em] text-clay">INVOICING + SECURE PAYMENTS POWERED BY STRIPE.</p>
            </Reveal>

            <Reveal delay={0.12}>
              {status === "success" ? (
                <div className="h-full min-h-[380px] border border-gold/40 bg-card/60 p-10 flex flex-col items-center justify-center text-center animate-pulse-glow" data-testid="partner-success">
                  <span className="w-16 h-16 rounded-full bg-gold flex items-center justify-center">
                    <Handshake size={26} className="text-obsidian" />
                  </span>
                  <h3 className="mt-8 font-display text-4xl text-cream tracking-wide">LET'S TALK BUSINESS.</h3>
                  <p className="mt-4 text-sand font-light max-w-xs">Your inquiry is on the desk. Expect a response from the Cobain team directly.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="partner-form" noValidate>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="NAME *" error={errors.name} testId="pt-name">
                      <input className={inputClasses} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="pt-name-input" />
                    </Field>
                    <Field label="EMAIL *" error={errors.email} testId="pt-email">
                      <input className={inputClasses} type="email" value={form.email} onChange={set("email")} placeholder="you@brand.com" data-testid="pt-email-input" />
                    </Field>
                    <Field label="PHONE" testId="pt-phone">
                      <input className={inputClasses} value={form.phone} onChange={set("phone")} placeholder="Optional" data-testid="pt-phone-input" />
                    </Field>
                    <Field label="COMPANY / BRAND" testId="pt-company">
                      <input className={inputClasses} value={form.company} onChange={set("company")} placeholder="Brand name" data-testid="pt-company-input" />
                    </Field>
                  </div>
                  <Field label="WEBSITE" testId="pt-website">
                    <input className={inputClasses} value={form.website} onChange={set("website")} placeholder="https://" data-testid="pt-website-input" />
                  </Field>
                  <Field label="PARTNERSHIP TYPE" testId="pt-subject">
                    <select className={`${inputClasses} appearance-none`} value={form.subject} onChange={set("subject")} data-testid="pt-subject-select">
                      <option value="">Select one</option>
                      {OFFERINGS.map((o) => (
                        <option key={o} value={o} className="bg-wood">{o}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="WHAT DO YOU HAVE IN MIND? *" error={errors.message} testId="pt-message">
                    <textarea className={`${inputClasses} min-h-[110px] resize-y`} value={form.message} onChange={set("message")} placeholder="Campaign, timeline, budget range — whatever you have" data-testid="pt-message-textarea" />
                  </Field>
                  {status === "error" && (
                    <p className="font-mono text-[11px] text-burnt-hover" data-testid="pt-form-error">Submission glitched. Try again.</p>
                  )}
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="pt-submit"
                    className="w-full flex items-center justify-center gap-3 bg-gold hover:bg-gold-bright text-obsidian font-mono text-xs tracking-[0.25em] font-bold px-8 py-5 rounded-full transition-all duration-300 hover:shadow-[0_0_36px_rgba(230,161,0,0.4)] disabled:opacity-60"
                  >
                    {status === "loading" ? "SENDING..." : "PARTNER WITH COBAIN"}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
